import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useScroll } from 'framer-motion';
import { MenuIcon, XIcon, ArrowRightIcon, SparklesIcon } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

const navLinks = [
  { label: 'Features', href: '#features' },
  { label: 'Solutions', href: '#solutions' },
  { label: 'Services', href: '#services' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'About', href: '#about' }
];

export function Navbar() {
  const { isDark } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const { scrollY } = useScroll();

  useEffect(() => {
    let ticking = false;
    const unsub = scrollY.on('change', (latest) => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(latest > 60);
          ticking = false;
        });
        ticking = true;
      }
    });
    return () => unsub();
  }, [scrollY]);

  return (
    <header className="fixed top-0 left-0 right-0 z-[100] px-3 sm:px-5 py-3 sm:py-4 pointer-events-none">
      <motion.nav
        initial={{ y: -110, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 110, damping: 22 }}
        className={`mx-auto max-w-7xl pointer-events-auto relative overflow-hidden transition-all duration-500 ${scrolled
          ? `rounded-2xl border-2 shadow-2xl py-2.5 px-4 md:px-6 ${isDark
            ? 'bg-slate-900/90 border-slate-700/50 backdrop-blur-xl'
            : 'bg-white/90 border-slate-200/60 backdrop-blur-xl'
          }`
          : 'bg-transparent py-3 px-2'
          }`}
        style={{
          boxShadow: scrolled
            ? isDark
              ? '0 10px 50px rgba(0,0,0,0.6), 0 0 0 1px rgba(6,182,212,0.08)'
              : '0 10px 45px rgba(15,30,80,0.12)'
            : 'none',
        }}
      >
        {/* Top gradient line */}
        <AnimatePresence>
          {scrolled && (
            <motion.div
              key="top-line"
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              exit={{ scaleX: 0, opacity: 0 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="absolute top-0 left-0 right-0 h-[2px] origin-left pointer-events-none"
              style={{
                background: isDark
                  ? 'linear-gradient(90deg, transparent 0%, rgba(6,182,212,0.7) 30%, rgba(99,102,241,0.7) 70%, transparent 100%)'
                  : 'linear-gradient(90deg, transparent 0%, rgba(6,182,212,0.8) 30%, rgba(99,102,241,0.8) 70%, transparent 100%)',
              }}
            />
          )}
        </AnimatePresence>

        {/* Radial ambient overlay */}
        {scrolled && (
          <div
            className="absolute inset-0 pointer-events-none opacity-30"
            style={{
              background: isDark
                ? 'radial-gradient(ellipse at 25% 50%, rgba(6,182,212,0.1) 0%, transparent 50%), radial-gradient(ellipse at 75% 50%, rgba(99,102,241,0.08) 0%, transparent 50%)'
                : 'radial-gradient(ellipse at 25% 50%, rgba(6,182,212,0.08) 0%, transparent 50%), radial-gradient(ellipse at 75% 50%, rgba(99,102,241,0.06) 0%, transparent 50%)',
            }}
          />
        )}

        <div className="flex items-center justify-between relative z-10 gap-3">
          {/* Logo */}
          <a
            href="#"
            className="flex items-center gap-3 group flex-shrink-0"
          >
            <div className="relative w-10 h-10 sm:w-11 sm:h-11 flex-shrink-0">
              {/* Pulsing glow ring */}
              <div
                className={`absolute -inset-1 rounded-2xl pointer-events-none opacity-40 group-hover:opacity-80 transition-all duration-500 ${isDark ? 'bg-cyan-500/30' : 'bg-cyan-400/30'
                  }`}
                style={{ filter: 'blur(8px)' }}
              />

              {/* Logo container */}
              <div
                className={`relative w-full h-full rounded-xl overflow-hidden flex items-center justify-center border-2 transition-all duration-500 group-hover:scale-110 group-hover:rotate-[-3deg] ${isDark
                  ? 'bg-gradient-to-br from-slate-900 to-slate-800 border-cyan-500/30 group-hover:border-cyan-400/60'
                  : 'bg-gradient-to-br from-white to-slate-50 border-slate-200 group-hover:border-cyan-400/60'
                  }`}
                style={{
                  boxShadow: isDark
                    ? '0 0 20px rgba(6,182,212,0.2)'
                    : '0 4px 16px rgba(6,182,212,0.12)',
                }}
              >
                <img
                  src="/Media.png"
                  alt="1401 Logo"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  style={{
                    filter: isDark
                      ? 'drop-shadow(0 0 8px rgba(6,182,212,0.6))'
                      : 'drop-shadow(0 0 6px rgba(6,182,212,0.4))',
                  }}
                />
              </div>
            </div>

            {/* Wordmark */}
            <span
              className={`text-xl sm:text-2xl font-black tracking-tighter transition-all duration-300 ${isDark ? 'text-white' : 'text-slate-900'
                }`}
            >
              14
              <span
                className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400"
              >
                01
              </span>
            </span>
          </a>

          {/* Desktop Nav */}
          <div
            className={`hidden lg:flex items-center gap-1 rounded-2xl p-1.5 border-2 transition-all duration-300 ${isDark
              ? 'bg-slate-800/50 border-slate-700/50 backdrop-blur-md'
              : 'bg-slate-100/70 border-slate-200/60 backdrop-blur-md'
              }`}
          >
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onMouseEnter={() => setHoveredLink(link.label)}
                onMouseLeave={() => setHoveredLink(null)}
                className={`relative px-4 py-2 text-sm font-semibold rounded-xl select-none transition-colors duration-200 ${hoveredLink === link.label
                  ? isDark ? 'text-white' : 'text-slate-900'
                  : isDark ? 'text-slate-400' : 'text-slate-500'
                  }`}
              >
                {hoveredLink === link.label && (
                  <motion.div
                    layoutId="nav-pill"
                    initial={false}
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    className={`absolute inset-0 rounded-xl ${isDark
                      ? 'bg-slate-700/50 shadow-lg shadow-cyan-500/20'
                      : 'bg-white shadow-md border border-white/80'
                      }`}
                    style={{ zIndex: -1 }}
                  />
                )}
                {link.label}
              </a>
            ))}
          </div>
          {/* Right Actions */}
          <div className="flex items-center gap-2 sm:gap-2.5 flex-shrink-0">
            {/* Register Today CTA */}
            <a
              href="panel.html#/login"
              className={`group flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-3 rounded-xl text-[10px] sm:text-xs font-bold text-white relative overflow-hidden transition-all duration-300 transform hover:scale-105 active:scale-95 ${isDark
                  ? 'shadow-[0_0_15px_rgba(6,182,212,0.6)]'
                  : 'shadow-[0_0_15px_rgba(59,130,246,0.6)]'
                }`}
              style={{
                background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 50%, #6366f1 100%)',
              }}
            >
              {/* Sparkles icon */}
              <SparklesIcon size={14} className="relative z-10 animate-pulse" />

              {/* Button text */}
              <span className="relative z-10">Register Today</span>

              {/* Arrow icon */}
              <ArrowRightIcon
                size={14}
                className="relative z-10 transform group-hover:translate-x-1 transition-transform duration-300"
              />

              {/* Shimmer effect */}
              <div
                className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                }}
              />

              {/* Glow pulse animation */}
              <span className="absolute inset-0 rounded-xl bg-white/10 opacity-0 group-hover:opacity-100 animate-ping-slow pointer-events-none" />
            </a>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden p-2.5 rounded-xl border-2 transition-all duration-200 active:scale-95 ${isDark
                ? 'bg-slate-800/50 border-slate-700/50 text-white hover:bg-slate-700/50'
                : 'bg-white border-slate-200 text-slate-700 shadow-sm hover:bg-slate-50'
                }`}
            >
              <AnimatePresence mode="wait">
                {mobileOpen ? (
                  <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.18 }}>
                    <XIcon size={18} />
                  </motion.div>
                ) : (
                  <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.18 }}>
                    <MenuIcon size={18} />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0, y: -10 }}
              animate={{ opacity: 1, height: 'auto', y: 0 }}
              exit={{ opacity: 0, height: 0, y: -10 }}
              transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              className={`lg:hidden mt-3 rounded-2xl border-2 overflow-hidden backdrop-blur-xl ${isDark
                ? 'bg-slate-900/95 border-slate-700/50'
                : 'bg-white/95 border-slate-200/70'
                }`}
              style={{
                boxShadow: isDark
                  ? '0 20px 60px rgba(0,0,0,0.5)'
                  : '0 20px 50px rgba(15,30,80,0.15)',
              }}
            >
              <div className="p-3 flex flex-col gap-1">
                {navLinks.map((link, idx) => (
                  <motion.a
                    key={link.label}
                    href={link.href}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05, ease: 'easeOut' }}
                    onClick={() => setMobileOpen(false)}
                    className={`px-4 py-3 rounded-xl text-base font-semibold transition-all duration-200 ${isDark
                      ? 'text-slate-300 hover:text-white hover:bg-slate-800/50'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                  >
                    {link.label}
                  </motion.a>
                ))}

                <div className={`h-px my-2 mx-1 ${isDark ? 'bg-slate-700/50' : 'bg-slate-200/70'}`} />

                <motion.a
                  href="panel.html#/login"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: navLinks.length * 0.05 + 0.06 }}
                  onClick={() => setMobileOpen(false)}
                  className="w-full py-3.5 rounded-xl font-bold text-white text-center flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                  style={{
                    background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 50%, #6366f1 100%)',
                    boxShadow: '0 6px 24px rgba(6,182,212,0.4)',
                  }}
                >
                  <SparklesIcon size={16} />
                  Register Today
                </motion.a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav >
    </header >
  );
}